import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useUserLocation from '../../hooks/useUserLocation';
import { supportChatApi } from '../../services/supportChatService';
import ChatWidgetShell from './ChatWidgetShell';
import {
  createChatMessage,
  buildChatHistory,
  loadStoredChat,
  persistChat,
  formatChatDisplayText,
} from '../../utils/chatWidgetUtils';
import {
  inferGuestConciergeHints,
  buildBrowseVendorsPath,
  buildGuestConciergeLocationOptions,
} from '../../utils/guestConciergeHints';

const STORAGE_PREFIX = 'support-chat-history';

const welcomeForRole = (role, name, enabled, locationOptions) => {
  const greeting = name ? `Hi ${name.split(' ')[0]}!` : 'Hi there!';
  if (!enabled) {
    return `${greeting} Support chat is temporarily unavailable. Please use the Help Center or contact support by email.`;
  }
  if (role === 'vendor') {
    return `${greeting} I'm your vendor assistant. Ask me anything—e.g. **How do I upgrade my plan?**, payouts, bookings, invoices, or services.`;
  }

  const regionHint = locationOptions?.regionDisplay
    ? ` in **${locationOptions.regionDisplay}**`
    : '';

  return `${greeting} I'm your support assistant.

Ask me to **find vendors** (e.g. "event planner in Accra") and I'll show matching services${regionHint} you can book right away. I can also help with bookings, payments, and your account.

Try a quick option below or describe what you need.`;
};

const buildWelcomeMessage = (role, name, enabled, locationOptions) =>
  createChatMessage(welcomeForRole(role, name, enabled, locationOptions), 'bot', {
    isWelcome: true,
    actions: role === 'client' ? locationOptions?.welcomeActions : undefined,
  });

const VENDOR_QUICK_PROMPTS = [
  'How do I upgrade my plan?',
  'Where are my payouts?',
  'How do I create an invoice?',
];

const buildClientQuickPrompts = (locationOptions) => {
  if (locationOptions?.cityDisplay) {
    return [
      `Event planner in ${locationOptions.cityDisplay}`,
      `Photo booth in ${locationOptions.cityDisplay}`,
      'Where are my bookings?',
    ];
  }
  return ['Event planner in Accra', 'Photo booth near me', 'How do I pay my balance?'];
};

const buildClientLocationOptions = (userLocation) => {
  const base = buildGuestConciergeLocationOptions(userLocation);
  if (!base.citySlug) {
    return {
      ...base,
      welcomeActions: [{ label: 'Browse all vendors', path: '/browse-vendors', type: 'link' }],
    };
  }

  const browsePath = `/browse-vendors?city=${encodeURIComponent(base.citySlug)}`;
  return {
    ...base,
    welcomeActions: [
      { label: `Browse vendors in ${base.cityDisplay}`, path: browsePath, type: 'link' },
    ],
  };
};

const ChatWidget = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { userLocation } = useUserLocation();
  const role = user?.user_type;
  const isClient = role === 'client';
  const isSupportedUser = isAuthenticated && (role === 'client' || role === 'vendor');

  const locationOptions = useMemo(
    () => (isClient ? buildClientLocationOptions(userLocation) : null),
    [isClient, userLocation?.city, userLocation?.country, userLocation?.state, userLocation?.display]
  );

  const storageKey = user?.id ? `${STORAGE_PREFIX}:${user.id}` : null;

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [assistantLabel, setAssistantLabel] = useState('AI Assistant');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);
  const [hints, setHints] = useState({});

  const abortRef = useRef(null);

  useEffect(() => {
    if (!isSupportedUser || !storageKey) return;

    let cancelled = false;

    const init = async () => {
      let enabled = true;
      let label = role === 'vendor' ? 'Vendor Assistant' : 'Support Assistant';
      try {
        const res = await supportChatApi.getStatus();
        enabled = Boolean(res?.data?.enabled);
        label = res?.data?.assistant_name || label;
      } catch {
        enabled = false;
      }
      if (cancelled) return;
      setChatEnabled(enabled);
      setAssistantLabel(label);
      const welcome = buildWelcomeMessage(role, user?.name, enabled, locationOptions);
      setMessages(loadStoredChat(storageKey, welcome));
      if (isClient && locationOptions?.citySlug) {
        setHints({ city: locationOptions.citySlug });
      }
      setStatusChecked(true);
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [isSupportedUser, storageKey, role, user?.name, isClient, locationOptions]);

  useEffect(() => {
    persistChat(storageKey, messages);
  }, [messages, storageKey]);

  useEffect(() => {
    const handleOpenChat = () => {
      setIsOpen(true);
      setIsMinimized(false);
    };
    window.addEventListener('openChatWidget', handleOpenChat);
    return () => window.removeEventListener('openChatWidget', handleOpenChat);
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  const inferHints = useCallback(
    (text, prev) =>
      isClient
        ? inferGuestConciergeHints(text, prev, locationOptions?.citySlug || null)
        : prev,
    [isClient, locationOptions?.citySlug]
  );

  const sendToAssistant = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;

      if (!chatEnabled) {
        setError('AI chat is not configured. Add GEMINI_API_KEY to the API .env file.');
        return;
      }

      const isNearMePrompt = /\b(near me|around me|my area|my location|local vendors?|services? near)\b/i.test(
        trimmed
      );
      const messageForApi =
        isNearMePrompt && locationOptions?.cityDisplay
          ? trimmed.replace(/\bnear me\b/gi, `in ${locationOptions.cityDisplay}`)
          : trimmed;

      const nextHints = isClient ? inferHints(messageForApi, hints) : hints;
      if (isClient && isNearMePrompt && locationOptions?.citySlug) {
        nextHints.city = locationOptions.citySlug;
      }
      if (isClient) {
        setHints(nextHints);
      }

      setError(null);
      setMessages((prev) => [...prev, createChatMessage(trimmed, 'user')]);
      setInputValue('');
      setIsSending(true);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await supportChatApi.sendMessage({
          message: messageForApi,
          history: buildChatHistory(messages),
          hints: isClient ? nextHints : undefined,
          signal: controller.signal,
        });

        const reply = formatChatDisplayText(res?.data?.reply);
        if (!reply) throw new Error('Empty response from assistant.');

        const browsePath = buildBrowseVendorsPath(nextHints);
        const actions = isClient
          ? (res?.data?.actions || []).map((action) =>
              action.path === '/browse-vendors' && nextHints.city
                ? { ...action, path: browsePath }
                : action
            )
          : undefined;

        setMessages((prev) => [
          ...prev,
          createChatMessage(reply, 'bot', {
            actions,
            suggestions: isClient ? res?.data?.suggestions || [] : undefined,
          }),
        ]);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setError(err?.response?.data?.message || err?.message || 'Something went wrong.');
        setMessages((prev) => [
          ...prev,
          createChatMessage(
            isClient
              ? "I couldn't find recommendations right now. Try browsing vendors or ask about your bookings."
              : "I couldn't complete that request. Try again or visit the Help Center.",
            'bot',
            {
              isError: true,
              actions: isClient
                ? [
                    {
                      label: locationOptions?.cityDisplay
                        ? `Browse vendors in ${locationOptions.cityDisplay}`
                        : 'Browse matching vendors',
                      path: buildBrowseVendorsPath({
                        city: locationOptions?.citySlug || nextHints.city,
                      }),
                      type: 'link',
                    },
                  ]
                : undefined,
            }
          ),
        ]);
      } finally {
        setIsSending(false);
        abortRef.current = null;
      }
    },
    [chatEnabled, hints, inferHints, isClient, isSending, locationOptions, messages]
  );

  const handleClear = () => {
    abortRef.current?.abort();
    setIsSending(false);
    setError(null);
    setMessages([
      buildWelcomeMessage(role, user?.name, chatEnabled, locationOptions),
    ]);
    if (isClient) {
      setHints(locationOptions?.citySlug ? { city: locationOptions.citySlug } : {});
    }
    if (storageKey) sessionStorage.removeItem(storageKey);
  };

  if (authLoading || !isSupportedUser || !statusChecked) {
    return null;
  }

  const quickPrompts =
    role === 'vendor' ? VENDOR_QUICK_PROMPTS : buildClientQuickPrompts(locationOptions);
  const showQuickPrompts =
    !messages.some((message) => message.sender === 'user') && !isSending;

  return (
    <ChatWidgetShell
      variant="support"
      isOpen={isOpen}
      isMinimized={isMinimized}
      chatEnabled={chatEnabled}
      assistantLabel={assistantLabel}
      subtitle={chatEnabled ? 'Powered by Gemini' : 'Offline'}
      messages={messages}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onSend={sendToAssistant}
      isSending={isSending}
      error={error}
      onToggle={() => {
        if (isOpen && !isMinimized) setIsMinimized(true);
        else if (isOpen && isMinimized) setIsMinimized(false);
        else {
          setIsOpen(true);
          setIsMinimized(false);
        }
      }}
      onClose={() => {
        abortRef.current?.abort();
        setIsOpen(false);
        setIsMinimized(false);
      }}
      onClear={handleClear}
      quickPrompts={showQuickPrompts ? quickPrompts : []}
      onQuickPrompt={sendToAssistant}
    />
  );
};

export default ChatWidget;
